import React, {FC} from 'react';

import CardButton from '@/components/CardButton';
import Space from '@/components/Space';
import TransparentButton from '@/components/TransparentButton';
import WarningBox from '@/components/WarningBox';
import CollectLayout from '@/modules/Collect/components/CollectLayout';
import {CollectError} from '@/modules/Collect/errors';
import {useCollectState} from '@/modules/Collect/useCollectState';
import {EtherscanLink, JiffyscanLink} from '@/modules/Transactions';
import {PasskeyError} from '@/services/passkey';
import {useThemedStyles} from '@/theme';
import {isOSXBrowser} from '@/utils/platform';
import {getUserDisplayName} from '@/utils/user';

import {styles} from './TransactionErrorInfo.style';

const TransactionErrorInfo: FC = () => {
  const style = useThemedStyles(styles);

  const {
    transaction,
    user,
    selectedOffer,
    collectInfo,
    closeCollectModal,
    resetTxDetails,
  } = useCollectState();

  const {error, txHash, userOpHash} = transaction;

  const chainId = selectedOffer?.chainId;

  if (error instanceof PasskeyError && error.status === 'MISSING_BLOB') {
    return (
      <CollectLayout
        minimized
        style={style.actions}
        header={
          <WarningBox
            title={{id: 'collect.error.missingBlob'}}
            message={{
              id: isOSXBrowser
                ? 'collect.error.missingBlob.osx'
                : 'collect.error.missingBlob.default',
            }}
          />
        }>
        <CardButton
          onPress={resetTxDetails}
          text={{id: 'collect.error.tryAgain'}}
        />
        <TransparentButton
          style={style.secondaryAction}
          onPress={closeCollectModal}
          text={{id: 'collect.error.goBack'}}
        />
      </CollectLayout>
    );
  }

  if (
    error instanceof CollectError &&
    error.status === 'WRONG_PASSKEY_SIGNER'
  ) {
    return (
      <CollectLayout
        minimized
        style={style.actions}
        header={
          <WarningBox
            title={{id: 'collect.error.wrongSigner'}}
            message={{
              id: 'collect.error.wrongSigner.message',
              values: {user: user ? getUserDisplayName(user) : ''},
            }}
          />
        }>
        <CardButton
          onPress={resetTxDetails}
          text={{id: 'collect.error.tryAgain'}}
        />
        <TransparentButton
          style={style.secondaryAction}
          onPress={closeCollectModal}
          text={{id: 'collect.error.goBack'}}
        />
      </CollectLayout>
    );
  }

  if (error instanceof CollectError && error.status === 'OFFER_NOT_AVAILABLE') {
    return (
      <CollectLayout
        minimized
        style={style.actions}
        header={
          <WarningBox
            title={{id: 'collect.error.notAvailable'}}
            message={{id: 'collect.error.notAvailable.message'}}
          />
        }>
        {collectInfo?.availability === 'direct' ? (
          <>
            <CardButton
              onPress={resetTxDetails}
              text={{id: 'collect.error.notAvailable.change'}}
            />
            <TransparentButton
              style={style.secondaryAction}
              onPress={closeCollectModal}
              text={{id: 'collect.error.goBack'}}
            />
          </>
        ) : collectInfo?.availability === 'external' ||
          collectInfo?.availability === 'unknown' ? (
          <>
            <CardButton
              onPress={resetTxDetails}
              text={{id: 'collect.error.notAvailable.details'}}
            />
            <TransparentButton
              style={style.secondaryAction}
              onPress={closeCollectModal}
              text={{id: 'collect.error.goBack'}}
            />
          </>
        ) : (
          <CardButton
            onPress={closeCollectModal}
            text={{id: 'collect.error.goBack'}}
          />
        )}
      </CollectLayout>
    );
  }

  if (error instanceof CollectError && error.status === 'OFFER_PRICE_CHANGED') {
    return (
      <CollectLayout
        minimized
        style={style.actions}
        header={
          <WarningBox
            title={{id: 'collect.error.offerChanged'}}
            message={{id: 'collect.error.offerChanged.message'}}
          />
        }>
        <CardButton
          onPress={resetTxDetails}
          text={{id: 'collect.error.seeDetails'}}
        />
        <TransparentButton
          style={style.secondaryAction}
          onPress={closeCollectModal}
          text={{id: 'collect.error.goBack'}}
        />
      </CollectLayout>
    );
  }

  if (
    error instanceof CollectError &&
    error.status === 'APPROVAL_TRANSACTION_REVERTED'
  ) {
    return (
      <CollectLayout
        minimized
        style={style.actions}
        header={
          <WarningBox
            title={{id: 'collect.error.approval.reverted'}}
            action={
              error.transactionDetails && (
                <Space mt="xs">
                  <EtherscanLink
                    txHash={error.transactionDetails.txHash}
                    chainId={error.transactionDetails.chainId}
                  />
                </Space>
              )
            }
          />
        }>
        <CardButton
          onPress={resetTxDetails}
          text={{id: 'collect.error.tryAgain'}}
        />
        <TransparentButton
          style={style.secondaryAction}
          onPress={closeCollectModal}
          text={{id: 'collect.error.goBack'}}
        />
      </CollectLayout>
    );
  }

  if (
    error instanceof CollectError &&
    error.status === 'TRANSACTION_REVERTED'
  ) {
    return (
      <CollectLayout
        minimized
        style={style.actions}
        header={
          <WarningBox
            title={{id: 'collect.error.reverted'}}
            action={
              error.transactionDetails && (
                <Space mt="xs">
                  <EtherscanLink
                    txHash={error.transactionDetails.txHash}
                    chainId={error.transactionDetails.chainId}
                  />
                </Space>
              )
            }
          />
        }>
        <CardButton
          onPress={resetTxDetails}
          text={{id: 'collect.error.tryAgain'}}
        />
        <TransparentButton
          style={style.secondaryAction}
          onPress={closeCollectModal}
          text={{id: 'collect.error.goBack'}}
        />
      </CollectLayout>
    );
  }

  return (
    <CollectLayout
      minimized
      style={style.actions}
      header={
        <WarningBox
          title={{id: 'collect.error.generic'}}
          message={{id: 'collect.error.generic.message'}}
          action={
            txHash && chainId ? (
              <Space mt="xs">
                <EtherscanLink txHash={txHash} chainId={chainId} />
              </Space>
            ) : userOpHash && chainId ? (
              <Space mt="xs">
                <JiffyscanLink userOpHash={userOpHash} chainId={chainId} />
              </Space>
            ) : null
          }
        />
      }>
      <CardButton
        onPress={resetTxDetails}
        text={{id: 'collect.error.tryAgain'}}
      />
      <TransparentButton
        style={style.secondaryAction}
        onPress={closeCollectModal}
        text={{id: 'collect.error.goBack'}}
      />
    </CollectLayout>
  );
};

export default TransactionErrorInfo;
